import { Icon, cn } from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ImagePreviewTrigger } from "@/features/identity-hub/components/dashboard/ImagePreviewTrigger";
import { PersonaPortrait } from "@/features/personas/components/PersonaPortrait/PersonaPortrait";
import { PersonaStaleBadge } from "@/features/personas/components/PersonaStaleBadge/PersonaStaleBadge";
import type { PersonaOpportunityPersonaGroup } from "../../../lib/persona-opportunity-row-model.js";
import {
  PersonaOpportunityAngleCountBadge,
  type PersonaOpportunityAngleStats,
} from "./PersonaOpportunityAngleCountBadge.js";

export interface PersonaOpportunityPersonaGroupHeaderProps {
  group: PersonaOpportunityPersonaGroup;
  expanded: boolean;
  onToggle: () => void;
  /** When true, show pains/motivations lists under the header (table view). */
  showProfileLists?: boolean;
  /** Overrides `group.opportunities.length` when the hub has a rollup before rows load. */
  angleCount?: number;
  /** Angle names for the count's hover. Defaults to `group.opportunities`; omit when only some are loaded. */
  angles?: readonly string[];
  /** Group KPIs for the count's hover, from the hub rollup. */
  angleStats?: PersonaOpportunityAngleStats | null;
  /** Shown when several sets are in view, so the same persona can be told apart. */
  setBadge?: ReactNode;
}

function ProfileReveal({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-150 ease-out motion-reduce:transition-none",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div
        className={cn(
          "min-h-0 overflow-hidden transition-opacity duration-150 ease-out motion-reduce:transition-none",
          open ? "opacity-100" : "opacity-0",
        )}
        inert={!open}
      >
        {children}
      </div>
    </div>
  );
}

export function PersonaOpportunityPersonaGroupHeader({
  group,
  expanded,
  onToggle,
  showProfileLists = false,
  angleCount: angleCountOverride,
  angles,
  angleStats = null,
  setBadge = null,
}: PersonaOpportunityPersonaGroupHeaderProps) {
  const { t } = useTranslation();
  const angleCount = angleCountOverride ?? group.opportunities.length;
  const hasProfile =
    showProfileLists && (group.pains.length > 0 || group.motivations.length > 0);

  return (
    <div className="rounded-md border border-border bg-muted/40">
      <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 px-4 py-4 text-left transition-colors duration-150 ease-out hover:bg-muted/60 motion-reduce:transition-none"
      >
        <Icon
          name="chevron-right"
          size="sm"
          className={cn(
            "mt-2.5 shrink-0 text-muted-foreground transition-transform duration-150 ease-out motion-reduce:transition-none",
            expanded && "rotate-90",
          )}
          aria-hidden
        />
        <span aria-hidden className="size-[42px] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-foreground">{group.title}</span>
            {setBadge}
            <PersonaOpportunityAngleCountBadge
              title={group.title}
              angleCount={angleCount}
              angles={angles ?? group.opportunities.map((opportunity) => opportunity.angle)}
              stats={angleStats}
            />
            {group.isStale ? <PersonaStaleBadge /> : null}
          </div>
          {group.who ? (
            <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{group.who}</p>
          ) : null}
        </div>
      </button>
      <div className={cn("absolute top-4 left-[42px]", !group.avatarUrl && "pointer-events-none")}>
        <ImagePreviewTrigger
          src={group.avatarUrl}
          alt={group.title}
          className="group/avatar rounded-full"
        >
          <span className="relative block transition-transform duration-150 ease-out motion-safe:group-hover/avatar:scale-105 motion-reduce:transition-none">
            <PersonaPortrait
              avatarUrl={group.avatarUrl}
              personalityKey={group.personalityKey}
              title={group.title}
              className="size-[42px] ring-1 ring-border transition-shadow duration-150 ease-out group-hover/avatar:ring-primary/50 motion-reduce:transition-none"
            />
            {group.avatarUrl ? (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-foreground/50 opacity-0 transition-opacity duration-150 ease-out group-hover/avatar:opacity-100 motion-reduce:transition-none"
              >
                <Icon name="maximize-2" size="xs" color="inherit" className="text-background" />
              </span>
            ) : null}
          </span>
        </ImagePreviewTrigger>
      </div>
      </div>

      {hasProfile ? (
        <ProfileReveal open={expanded}>
          <div className="grid gap-4 border-t border-border px-4 pb-4 pl-[4.75rem] pt-3 sm:grid-cols-2">
          {group.pains.length > 0 ? (
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.active.painPoints", "Pain points")}
              </p>
              <ul className="space-y-1 text-[12.5px] text-muted-foreground">
                {group.pains.map((pain) => (
                  <li key={pain}>· {pain}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {group.motivations.length > 0 ? (
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.active.motivations", "Motivations")}
              </p>
              <ul className="space-y-1 text-[12.5px] text-muted-foreground">
                {group.motivations.map((motivation) => (
                  <li key={motivation}>· {motivation}</li>
                ))}
              </ul>
            </div>
          ) : null}
          </div>
        </ProfileReveal>
      ) : null}
    </div>
  );
}
